import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Lightbulb, Check, X, TrendingDown, Pencil, Save, MessageSquare, Clock, Brain } from 'lucide-react'
import { api, apiFetchJson } from '@/lib/api-client'
import { formatDateTime } from '@/lib/utils'
import { PageHeader } from '@/components/ui/page-header'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge, SeverityBadge } from '@/components/ui/badge'
import { EmptyState } from '@/components/ui/empty-state'
import { formatCurrency } from '@roi365/shared'
import { toast } from 'sonner'
import { useState } from 'react'
import { useCanWrite } from '@/hooks/use-permissions'

interface Recommendation {
  id: string
  m365_tenant_id: string
  type: string
  severity: string
  title: string
  description: string
  estimated_monthly_saving: number
  status: string
  user_display_name: string | null
  user_principal_name: string | null
  license_display_name: string | null
  notes: string | null
  created_at: string
  // Vista unificada: 'rule_legacy' (tabla recommendations) o 'ai' (ai_recommendations promovida)
  source?: 'rule_legacy' | 'ai'
  category?: string | null
  confidence?: number | null
  accepted_at?: string | null
  // Hallazgo IA visible que referencia esta recomendación (o la propia, si es una IA promovida)
  ai_finding_id?: string | null
}

interface HistoryEntry {
  id: string
  field_name: string
  old_value: string | null
  new_value: string | null
  user_name: string | null
  created_at: string
}

interface SummaryEntry {
  status: string
  count: number
  total_saving: number
}

const typeLabels: Record<string, string> = {
  remove_license: 'Eliminar licencia',
  downgrade: 'Downgrade',
  disable_service: 'Deshabilitar servicio',
  reduce_purchased: 'Reducir compradas',
  remove_redundant: 'Eliminar redundante',
  alert: 'Alerta',
  alert_exchange: 'Infrautilización Exchange',
  alert_onedrive: 'Infrautilización OneDrive',
  alert_sharepoint: 'Infrautilización SharePoint',
  alert_teams: 'Infrautilización Teams',
  alert_copilot: 'Copilot sin uso',
}

const severityOptions = [
  { value: 'low', label: 'Baja' },
  { value: 'medium', label: 'Media' },
  { value: 'high', label: 'Alta' },
  { value: 'critical', label: 'Critica' },
]

const fieldLabels: Record<string, string> = {
  title: 'titulo',
  description: 'descripcion',
  severity: 'severidad',
  estimated_monthly_saving: 'ahorro estimado',
  notes: 'notas',
  status: 'estado',
}

export function RecommendationsPage() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const canWrite = useCanWrite()
  const [statusFilter, setStatusFilter] = useState('pending')
  const [typeFilter, setTypeFilter] = useState<string | null>(null)
  // Deep link from an AI finding: /recommendations?ids=a,b,c shows exactly those, whatever their status.
  const [idsFilter, setIdsFilter] = useState<string[]>(() => (new URLSearchParams(window.location.search).get('ids') ?? '').split(',').filter(Boolean))
  const clearIdsFilter = () => {
    setIdsFilter([])
    navigate({ to: '/recommendations', search: {} })
  }
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editForm, setEditForm] = useState({ title: '', description: '', severity: '', estimatedMonthlySaving: '', notes: '', status: '' })
  const [notesId, setNotesId] = useState<string | null>(null)
  const [noteText, setNoteText] = useState('')
  const [historyId, setHistoryId] = useState<string | null>(null)
  const [dismissId, setDismissId] = useState<string | null>(null)
  const [dismissReason, setDismissReason] = useState('')

  const { data: recs, isLoading } = useQuery({
    queryKey: ['recommendations', statusFilter, typeFilter, idsFilter.join(',')],
    queryFn: async () => {
      const params = new URLSearchParams({ perPage: '100' })
      if (idsFilter.length > 0) params.set('ids', idsFilter.join(','))
      else params.set('status', statusFilter)
      if (typeFilter) params.set('type', typeFilter)
      return apiFetchJson<{ ok: boolean; data: Recommendation[] }>(`/recommendations?${params}`)
    },
  })

  const { data: summary } = useQuery({
    queryKey: ['recommendations', 'summary'],
    queryFn: () => api.get<SummaryEntry[]>('/recommendations/summary'),
  })

  const { data: historyData } = useQuery({
    queryKey: ['recommendation-history', historyId],
    queryFn: () => api.get<HistoryEntry[]>(`/recommendations/${historyId}/history`),
    enabled: !!historyId,
  })

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['recommendations'] })
    queryClient.invalidateQueries({ queryKey: ['recommendation-history'] })
  }

  const acceptMutation = useMutation({
    mutationFn: (id: string) => api.put(`/recommendations/${id}/accept`),
    onSuccess: () => { toast.success('Recomendacion aplicada'); invalidate() },
  })

  // Dispatch del descarte según origen: legacy exige reason; IA usa el endpoint
  // /ai-recommendations/:id/dismiss y no requiere motivo (UX coherente con esa pagina).
  const dismissMutation = useMutation({
    mutationFn: ({ id, reason, source }: { id: string; reason: string; source?: string }) => {
      if (source === 'ai') {
        return api.put(`/ai-recommendations/${id}/dismiss`)
      }
      return api.put(`/recommendations/${id}/dismiss`, { reason })
    },
    onSuccess: () => {
      toast.success('Recomendacion descartada')
      invalidate()
      setDismissId(null)
      setDismissReason('')
    },
    onError: (err) => toast.error((err as Error).message),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      api.put(`/recommendations/${id}`, data),
    onSuccess: () => { toast.success('Recomendacion actualizada'); invalidate(); setEditingId(null) },
    onError: (err) => toast.error((err as Error).message),
  })

  const notesMutation = useMutation({
    mutationFn: ({ id, notes }: { id: string; notes: string }) =>
      api.put(`/recommendations/${id}`, { notes }),
    onSuccess: () => { toast.success('Comentario guardado'); invalidate(); setNotesId(null); setNoteText('') },
    onError: (err) => toast.error((err as Error).message),
  })

  const startEdit = (rec: Recommendation) => {
    setEditingId(rec.id)
    setEditForm({
      title: rec.title,
      description: rec.description,
      severity: rec.severity,
      estimatedMonthlySaving: rec.estimated_monthly_saving.toString(),
      notes: rec.notes ?? '',
      status: rec.status,
    })
  }

  const saveEdit = (id: string) => {
    updateMutation.mutate({
      id,
      data: {
        title: editForm.title,
        description: editForm.description,
        severity: editForm.severity,
        estimatedMonthlySaving: parseFloat(editForm.estimatedMonthlySaving) || 0,
        notes: editForm.notes || null,
        status: editForm.status,
      },
    })
  }

  const startNotes = (rec: Recommendation) => {
    setNotesId(rec.id)
    setNoteText(rec.notes ?? '')
  }

  const currentSummary = summary?.find((s) => s.status === statusFilter)
  const data = recs?.data ?? []

  const savingLabels: Record<string, string> = {
    pending: 'Ahorro potencial mensual',
    accepted: 'Ahorro aceptado mensual',
    dismissed: 'Ahorro descartado mensual',
    applied: 'Ahorro aplicado mensual',
    resolved: 'Ahorro resuelto mensual',
    expired: 'Ahorro expirado mensual',
  }
  const countLabels: Record<string, string> = {
    pending: 'Recomendaciones pendientes',
    accepted: 'Recomendaciones aceptadas',
    dismissed: 'Recomendaciones descartadas',
    applied: 'Recomendaciones aplicadas',
    resolved: 'Recomendaciones resueltas',
    expired: 'Recomendaciones expiradas',
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Recomendaciones"
        description="Optimizaciones detectadas para reducir costes de licencias"
      />

      {/* Linked-from-AI filter */}
      {idsFilter.length > 0 && (
        <Card>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              Mostrando solo las {idsFilter.length} recomendación(es) vinculadas al hallazgo IA, en cualquier estado.
            </p>
            <Button variant="secondary" size="sm" onClick={clearIdsFilter}><X className="h-3.5 w-3.5" /> Quitar filtro</Button>
          </div>
        </Card>
      )}

      {/* Summary */}
      {idsFilter.length === 0 && currentSummary && currentSummary.count > 0 && (
        <Card>
          <div className="flex items-center gap-4">
            <div
              className="flex h-12 w-12 items-center justify-center rounded-lg"
              style={{ backgroundColor: 'var(--color-brand-accent-2)', color: 'white' }}
            >
              <TrendingDown className="h-6 w-6" />
            </div>
            <div>
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                {savingLabels[statusFilter] ?? 'Ahorro mensual'}
              </p>
              <p className="text-2xl font-bold tabular-nums" style={{ color: 'var(--color-text-primary)' }}>
                {formatCurrency(currentSummary.total_saving)}
              </p>
            </div>
            <div className="ml-8">
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                {countLabels[statusFilter] ?? 'Recomendaciones'}
              </p>
              <p className="text-2xl font-bold tabular-nums" style={{ color: 'var(--color-text-primary)' }}>
                {currentSummary.count}
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Status Filters */}
      <div className="flex flex-wrap gap-2">
        {(['pending', 'accepted', 'dismissed', 'applied', 'resolved', 'expired'] as const).map((s) => {
          const labels: Record<string, string> = {
            pending: 'Pendientes',
            accepted: 'Aceptadas',
            dismissed: 'Descartadas',
            applied: 'Aplicadas',
            resolved: 'Resueltas Automáticas',
            expired: 'Expiradas',
          }
          return (
            <Button
              key={s}
              variant={idsFilter.length === 0 && statusFilter === s ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => { if (idsFilter.length > 0) clearIdsFilter(); setStatusFilter(s) }}
            >
              {labels[s]}
              {summary?.find((x) => x.status === s)?.count ? ` (${summary.find((x) => x.status === s)!.count})` : ''}
            </Button>
          )
        })}
      </div>

      {/* Type Filters */}
      <div className="flex flex-wrap gap-2">
        <span className="self-center text-xs font-medium" style={{ color: 'var(--color-text-tertiary)' }}>Tipo:</span>
        <Button
          variant={typeFilter === null ? 'primary' : 'secondary'}
          size="sm"
          onClick={() => setTypeFilter(null)}
        >
          Todos
        </Button>
        {Object.entries(typeLabels).map(([value, label]) => (
          <Button
            key={value}
            variant={typeFilter === value ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setTypeFilter(value)}
          >
            {label}
          </Button>
        ))}
      </div>

      {/* List */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 animate-pulse rounded-lg" style={{ backgroundColor: 'var(--color-gray-200)' }} />
          ))}
        </div>
      ) : data.length === 0 ? (
        <Card>
          <EmptyState
            icon={Lightbulb}
            title={statusFilter === 'pending' ? 'Sin recomendaciones pendientes' : 'Sin resultados'}
            description={statusFilter === 'pending' ? 'Conecta un tenant M365 y ejecuta una sincronizacion para generar recomendaciones' : undefined}
          />
        </Card>
      ) : (
        <div className="space-y-3">
          {data.map((rec) => {
            const isEditing = editingId === rec.id
            const isNotesOpen = notesId === rec.id

            return (
              <Card key={rec.id}>
                {isEditing ? (
                  /* ── Edit mode ── */
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div>
                        <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>Titulo</label>
                        <input
                          type="text" value={editForm.title}
                          onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
                          className="w-full rounded-md border px-3 py-2 text-sm"
                          style={{ borderColor: 'var(--color-border)' }}
                        />
                      </div>
                      <div className="flex gap-3">
                        <div className="flex-1">
                          <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>Severidad</label>
                          <select
                            value={editForm.severity}
                            onChange={(e) => setEditForm((f) => ({ ...f, severity: e.target.value }))}
                            className="w-full rounded-md border px-3 py-2 text-sm"
                            style={{ borderColor: 'var(--color-border)' }}
                          >
                            {severityOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                        </div>
                        <div className="flex-1">
                          <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>Ahorro/mes (EUR)</label>
                          <input
                            type="number" step="0.01" value={editForm.estimatedMonthlySaving}
                            onChange={(e) => setEditForm((f) => ({ ...f, estimatedMonthlySaving: e.target.value }))}
                            className="w-full rounded-md border px-3 py-2 text-sm tabular-nums"
                            style={{ borderColor: 'var(--color-border)' }}
                          />
                        </div>
                        <div className="flex-1">
                          <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>Estado</label>
                          <select
                            value={editForm.status}
                            onChange={(e) => setEditForm((f) => ({ ...f, status: e.target.value }))}
                            className="w-full rounded-md border px-3 py-2 text-sm"
                            style={{ borderColor: 'var(--color-border)' }}
                          >
                            <option value="pending">Pendiente</option>
                            <option value="accepted">Aceptada</option>
                            <option value="dismissed">Descartada</option>
                            <option value="applied">Aplicada</option>
                          </select>
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>Descripcion</label>
                      <textarea
                        value={editForm.description}
                        onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                        rows={2}
                        className="w-full rounded-md border px-3 py-2 text-sm"
                        style={{ borderColor: 'var(--color-border)' }}
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>Notas / Comentarios</label>
                      <textarea
                        value={editForm.notes}
                        onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))}
                        rows={2}
                        placeholder="Añadir comentarios internos..."
                        className="w-full rounded-md border px-3 py-2 text-sm"
                        style={{ borderColor: 'var(--color-border)' }}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button variant="primary" size="sm" onClick={() => saveEdit(rec.id)} loading={updateMutation.isPending}>
                        <Save className="h-3.5 w-3.5" /> Guardar
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setEditingId(null)}>
                        Cancelar
                      </Button>
                    </div>
                  </div>
                ) : (
                  /* ── View mode ── */
                  <>
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <SeverityBadge severity={rec.severity} />
                          <Badge variant="neutral">{typeLabels[rec.type] ?? rec.type}</Badge>
                          {rec.source === 'ai' && (
                            <Badge variant="premium">IA</Badge>
                          )}
                          {rec.license_display_name && (
                            <span className="text-xs font-medium" style={{ color: 'var(--color-brand-secondary)' }}>
                              {rec.license_display_name}
                            </span>
                          )}
                          {rec.ai_finding_id && (
                            <button type="button" className="inline-flex items-center gap-1 text-xs underline" style={{ color: 'var(--color-text-link)' }}
                              onClick={() => navigate({ to: '/ai-recommendations', search: { finding: rec.ai_finding_id! } })}>
                              <Brain className="h-3.5 w-3.5" /> Ver hallazgo IA
                            </button>
                          )}
                        </div>
                        <h4 className="mt-2 font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                          {rec.title}
                        </h4>
                        <p className="mt-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                          {rec.description}
                        </p>
                        {rec.user_display_name && (
                          <p className="mt-1 text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
                            Usuario:{' '}
                            <button
                              type="button"
                              onClick={() => navigate({ to: '/users', search: { email: rec.user_principal_name!, tenant: rec.m365_tenant_id } })}
                              className="underline hover:no-underline"
                              style={{ color: 'var(--color-text-link)' }}
                            >
                              {rec.user_display_name}
                            </button>
                            {rec.user_principal_name && <span> ({rec.user_principal_name})</span>}
                          </p>
                        )}
                        {/* Notes display */}
                        {rec.notes && !isNotesOpen && (
                          <div
                            className="mt-2 rounded-md px-3 py-2 text-sm"
                            style={{ backgroundColor: 'var(--color-gray-50)', color: 'var(--color-text-secondary)' }}
                          >
                            <MessageSquare className="mr-1 inline h-3.5 w-3.5" style={{ color: 'var(--color-text-tertiary)' }} />
                            {rec.notes}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <p className="text-lg font-bold tabular-nums" style={{ color: 'var(--color-brand-accent-2)' }}>
                          {rec.estimated_monthly_saving > 0 ? formatCurrency(rec.estimated_monthly_saving) + '/mes' : ''}
                        </p>
                        <div className="flex flex-wrap justify-end gap-1">
                          {canWrite && rec.status === 'pending' && (
                            <>
                              <Button variant="primary" size="sm" onClick={() => acceptMutation.mutate(rec.id)} loading={acceptMutation.isPending}>
                                <Check className="h-3.5 w-3.5" /> Aplicada
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  if (dismissId === rec.id) {
                                    setDismissId(null)
                                    setDismissReason('')
                                  } else {
                                    setDismissId(rec.id)
                                    setDismissReason('')
                                  }
                                }}
                              >
                                <X className="h-3.5 w-3.5" /> Descartar
                              </Button>
                            </>
                          )}
                          {canWrite && rec.status === 'accepted' && rec.source === 'ai' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => dismissMutation.mutate({ id: rec.id, reason: '', source: 'ai' })}
                              loading={dismissMutation.isPending}
                            >
                              <X className="h-3.5 w-3.5" /> Descartar
                            </Button>
                          )}
                          {canWrite && (
                            <>
                              <Button variant="secondary" size="sm" onClick={() => startEdit(rec)}>
                                <Pencil className="h-3.5 w-3.5" /> Editar
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => isNotesOpen ? setNotesId(null) : startNotes(rec)}>
                                <MessageSquare className="h-3.5 w-3.5" /> {rec.notes ? 'Editar nota' : 'Comentar'}
                              </Button>
                            </>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => setHistoryId(historyId === rec.id ? null : rec.id)}>
                            <Clock className="h-3.5 w-3.5" /> Historial
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Inline dismiss reason form */}
                    {dismissId === rec.id && (
                      <div className="mt-3 border-t pt-3" style={{ borderColor: 'var(--color-border)' }}>
                        <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                          Motivo del descarte <span style={{ color: 'var(--color-error)' }}>*</span>
                        </label>
                        <textarea
                          value={dismissReason}
                          onChange={(e) => setDismissReason(e.target.value)}
                          rows={2}
                          placeholder="Explica por que se descarta esta recomendacion..."
                          className="w-full rounded-md border px-3 py-2 text-sm"
                          style={{ borderColor: 'var(--color-border)' }}
                          autoFocus
                        />
                        <div className="mt-2 flex gap-2">
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => dismissMutation.mutate({ id: rec.id, reason: dismissReason.trim() })}
                            loading={dismissMutation.isPending}
                            disabled={!dismissReason.trim()}
                          >
                            <X className="h-3.5 w-3.5" /> Confirmar descarte
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => { setDismissId(null); setDismissReason('') }}>
                            Cancelar
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Inline notes editor */}
                    {isNotesOpen && (
                      <div className="mt-3 border-t pt-3" style={{ borderColor: 'var(--color-border)' }}>
                        <textarea
                          value={noteText}
                          onChange={(e) => setNoteText(e.target.value)}
                          rows={2}
                          placeholder="Escribe un comentario..."
                          className="w-full rounded-md border px-3 py-2 text-sm"
                          style={{ borderColor: 'var(--color-border)' }}
                          autoFocus
                        />
                        <div className="mt-2 flex gap-2">
                          <Button variant="primary" size="sm" onClick={() => notesMutation.mutate({ id: rec.id, notes: noteText })} loading={notesMutation.isPending}>
                            <Save className="h-3.5 w-3.5" /> Guardar nota
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setNotesId(null)}>Cancelar</Button>
                        </div>
                      </div>
                    )}

                    {/* Change history */}
                    {historyId === rec.id && (
                      <div className="mt-3 border-t pt-3" style={{ borderColor: 'var(--color-border)' }}>
                        <p className="mb-2 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--color-text-tertiary)' }}>
                          Historial de cambios
                        </p>
                        {historyData && historyData.length > 0 ? (
                          <div className="space-y-1.5">
                            {historyData.map((entry) => (
                              <div key={entry.id} className="flex gap-3 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                                <span className="shrink-0 tabular-nums" style={{ color: 'var(--color-text-tertiary)' }}>
                                  {formatDateTime(entry.created_at)}
                                </span>
                                <span>
                                  <strong>{entry.user_name ?? 'Sistema'}</strong>{' '}
                                  cambio <strong>{fieldLabels[entry.field_name] ?? entry.field_name}</strong>
                                  {entry.old_value != null && <> de <em>{entry.old_value}</em></>}
                                  {entry.new_value != null && <> a <em>{entry.new_value}</em></>}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>Sin cambios registrados</p>
                        )}
                      </div>
                    )}
                  </>
                )}
              </Card>
            )
          })}
        </div>
      )}
    </div>
  )
}
